import { useState } from 'react';
import axios from 'axios';
import ElementFactory from 'components/elements/ElementFactory';
import LoadingComponentOverlay from 'components/LoadingComponentOverlay';
import { useAppDispatch } from 'hooks/redux';
import useConfirmModal from 'hooks/useConfirmModal';
import Foco from 'react-foco';
import { ConfirmModalProps } from 'store/confirmModalSlice';
import { showErrorMessage } from 'store/errorModalSlice';
import { deleteElement } from 'store/rfiSlice';
import { ElementMode, elementTypes, RfiElementSelectionList } from 'types/RfiTypes';
import { Button, Toolbar, ToolbarItem, ToolbarSpacer } from '@progress/kendo-react-buttons';
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from '@progress/kendo-react-layout';
import RfiElement from '../../types/RfiElement';
import AddElementButton from './AddElementButton';

interface IElementContainerProps {
  element: RfiElement;
  designMode?: boolean;
  readOnly?: boolean;
  questionNumber?: number;
  userRole?: string;
  rfiId?: any;
  currentSection?: string;
  sectionElements?: Array<RfiElementSelectionList>;
  modifyElements?: (modifyAction: () => Promise<void>) => Promise<void>;
  rerender?: boolean;
  defaultValue?: string;
  showValue?: boolean;
  overrideRequired?: boolean;
}

const ElementContainer = (props: IElementContainerProps) => {
  // Hooks
  const dispatch = useAppDispatch();
  // const rfi = useAppSelector(selectRfi);

  const { open } = useConfirmModal();
  // const { openElementLibraryModal } = useElementLibrary();

  // State
  const [displayDesignToolbar, setDisplayDesignToolbar] =
    useState("notdisplayed");
  const [displayQAToolbar, setDisplayQAToolbar] = useState("notdisplayed");
  const [editElementMode, setEditElementMode] =
    useState<ElementMode>(undefined);
  const [displayTabBar, setDisplayTabBar] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [isModifying, setIsModifying] = useState(false);

  // Handlers
  const handleSelectTab = (e: TabStripSelectEventArguments) => {
    setSelectedTab(e.selected);

    switch (e.selected) {
      case 2:
        setEditElementMode("move");
        break;
      case 1:
        setEditElementMode("copy");
        break;
      case 0:
      default:
        setEditElementMode("edit");
        break;
    }
  };

  const handleDelete = async () => {
    const isConfirmed = await open({
      title: "Confirm Delete",
      message: "Are you sure you want to delete this element?",
      description: "Confirm delete element",
    } as ConfirmModalProps);

    if (isConfirmed) {
      setIsModifying(true);
      axios
        .delete(`/api/v1/elements/rfi/${props.element.elementId}`)
        .then((result) => {
          dispatch(
            deleteElement({
              elementId: props.element.elementId,
              section: props.currentSection ?? "",
            })
          );
        })
        .catch((error) => {
          dispatch(
            showErrorMessage(`There was an error deleting element - ${error}`)
          );
        })
        .finally(() => {
          setIsModifying(false);
        });
    }
  };

  // const showToolbar = (e: any) => {
  //   e.preventDefault();
  //   setDisplayDesignToolbar("displayed");
  // };

  // const hideToolbar = (e: any) => {
  //   //e.preventDefault();
  //   setDisplayDesignToolbar("notdisplayed");
  // };

  // Render
  return (
    <>
      <Foco
        onFocusOutside={() => {
          setDisplayDesignToolbar("notdisplayed");
          setDisplayQAToolbar("notdisplayed");
          setEditElementMode(undefined);
          setSelectedTab(0);
          setDisplayTabBar(false);
        }}
        onClickOutside={() => {
          setDisplayDesignToolbar("notdisplayed");
          setDisplayQAToolbar("notdisplayed");
          setEditElementMode(undefined);
          setSelectedTab(0);
          setDisplayTabBar(false);
        }}
      >
        {/* {isModifying && (
                 <LoadingComponent />
              )} */}
        <LoadingComponentOverlay active={isModifying}>
          <div
            className="question-container"
            tabIndex={props.designMode ? 0 : -1}
            onFocus={() => {
              if (props.designMode && !editElementMode /*&& !props.readOnly*/)
                setDisplayDesignToolbar("displayed");
              if (
                !props.designMode &&
                !editElementMode &&
                !props.readOnly &&
                props.element?.elementType === elementTypes.SURVEY
              )
                setDisplayQAToolbar("notdisplayed"); // display for bottom toolbar
            }}
            style={
              props.designMode /*&& !props.readOnly*/
                ? {
                    border: "1px solid skyblue",
                  }
                : {}
            }
          >
            <div className={displayDesignToolbar}>
              <Toolbar>
                <ToolbarSpacer />

                <ToolbarItem>
                  <Button
                    size="small"
                    fillMode="flat"
                    rounded="full"
                    icon="edit"
                    title="Edit"
                    onClick={() => {
                      setDisplayDesignToolbar("notdisplayed");
                      setEditElementMode("edit");
                      setDisplayTabBar(true);
                    }}
                  ></Button>
                </ToolbarItem>
                {!props.overrideRequired &&
                  !(
                    props.element.requiredInRfi ||
                    props.element.customerRequired
                  ) && (
                    <ToolbarItem>
                      <Button
                        size="small"
                        fillMode="flat"
                        rounded="full"
                        icon="delete"
                        title="Delete"
                        onClick={handleDelete}
                      ></Button>
                    </ToolbarItem>
                  )}
                <ToolbarItem>
                  <AddElementButton
                    rfiId={props.rfiId}
                    section={props.currentSection}
                    modifyElements={props.modifyElements}
                    targetElementId={props.element.elementId}
                    inElementStyle
                    setIsModifying={setIsModifying}
                  />
                </ToolbarItem>
              </Toolbar>
            </div>

            {displayTabBar && (
              <TabStrip
                selected={selectedTab}
                onSelect={handleSelectTab}
                tabContentStyle={{ display: "none" }}
              >
                <TabStripTab title="Edit"></TabStripTab>
                <TabStripTab title="Copy"></TabStripTab>
                <TabStripTab title="Move"></TabStripTab>
              </TabStrip>
            )}
            <div
              style={{
                paddingLeft: "10px",
              }}
            >
              <ElementFactory
                designMode={props.designMode}
                readOnly={props.readOnly}
                element={props.element}
                editElementMode={editElementMode}
                questionNumber={props.questionNumber}
                userRole={props.userRole ? props.userRole : "user"}
                rfiId={props.rfiId}
                modifyElements={props.modifyElements}
                currentSection={props.currentSection}
                sectionElements={props.sectionElements}
                rerender={props.rerender}
                defaultValue={props.defaultValue}
                showValue={props.showValue}
                overrideRequired={props.overrideRequired}
              />
            </div>
            <div className={displayQAToolbar}>
              <Toolbar>
                <ToolbarItem>
                  <Button
                    size="small"
                    fillMode="solid"
                    rounded="full"
                    themeColor="primary"
                  >
                    Open Q&A (#)
                  </Button>
                </ToolbarItem>
              </Toolbar>
            </div>
          </div>
        </LoadingComponentOverlay>
      </Foco>
    </>
  );
};

export const ReadOnlyElementContainer = (props: IElementContainerProps) => {
  return (
    <div
      style={{
        paddingLeft: "10px",
      }}
    >
      <ElementFactory
        readOnly
        element={props.element}
        questionNumber={props.questionNumber}
        showValue={props.showValue}
      />
    </div>
  );
};

export default ElementContainer;
